import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useSmartNotesSettings } from '@/hooks/useSmartNotesSettings';
import { useRequiredFields } from '@/hooks/useRequiredFields';
import { useUserSettings } from '@/hooks/useUserSettings';
import { Loader2, Plus, Edit2, Trash2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

interface SmartNotesTemplate {
  name: string;
  prompt: string;
  description: string;
}

const SMART_NOTES_TEMPLATES: SmartNotesTemplate[] = [
  {
    name: "Company Overview",
    prompt: "Summarize the company in 2 sentences.",
    description: "Quick overview based on publicly available information."
  },
  {
    name: "Recent News",
    prompt: "Find recent news or press releases from the company.",
    description: "Finds the latest updates or PR topics."
  },
  {
    name: "Leadership Summary",
    prompt: "Who is on the leadership team? List the key people.",
    description: "Identifies CXO names and roles."
  },
  {
    name: "Market Focus",
    prompt: "What market or segment is the company active in?",
    description: "Classifies industry or business focus."
  },
  {
    name: "Competitive Landscape",
    prompt: "Name 2–3 main competitors of this company.",
    description: "Context about competitors."
  },
  {
    name: "Funding or Investors",
    prompt: "Find information about funding rounds or investors.",
    description: "Helpful for startups and scaleups."
  },
  {
    name: "Sustainability / ESG",
    prompt: "Are there ESG or sustainability initiatives?",
    description: "For CSR or ESG-related research."
  },
  {
    name: "Partnerships & Clients",
    prompt: "Which partners or clients are publicly mentioned?",
    description: "Provides external relationship data."
  },
  {
    name: "Key Products or Services",
    prompt: "What are the main products or services?",
    description: "Shows the core of the business model."
  },
  {
    name: "Opportunity Summary",
    prompt: "Formulate in 2 sentences why this contact could be relevant.",
    description: "Condensed insight note with practical value."
  }
];

export function SmartNotesSettings() {
  const {
    isLoading,
    enableSmartNotes,
    autoRunSmartNotes,
    tasks,
    defaultTaskId,
    toggleSmartNotes,
    toggleAutoRun,
    addTask,
    updateTask,
    deleteTask,
    setDefaultTask,
  } = useSmartNotesSettings();

  const { syncWithSmartFeatures } = useRequiredFields();
  const { enableCategories } = useUserSettings();
  const { toast } = useToast();

  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<{ id: string; title: string; prompt: string } | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newPrompt, setNewPrompt] = useState('');

  const handleToggleSmartNotes = async (enabled: boolean) => {
    await toggleSmartNotes(enabled);
    
    // Auto-sync required fields
    await syncWithSmartFeatures(enabled, enableCategories);
    
    toast({
      title: enabled ? 'Smart Notes enabled' : 'Smart Notes disabled',
      description: enabled 
        ? 'Notes field automatically added to required fields' 
        : 'Notes field removed from required fields',
    });
  };

  const handleAdd = async () => {
    if (!newTitle.trim() || !newPrompt.trim()) return;
    
    const success = await addTask(newTitle.trim(), newPrompt.trim());
    if (success) {
      setNewTitle('');
      setNewPrompt('');
      setShowAddDialog(false);
    }
  };

  const handleEdit = async () => {
    if (!editingTask || !newTitle.trim() || !newPrompt.trim()) return;
    
    const success = await updateTask(editingTask.id, {
      title: newTitle.trim(),
      prompt: newPrompt.trim(),
    });
    
    if (success) {
      setShowEditDialog(false);
      setEditingTask(null);
      setNewTitle('');
      setNewPrompt('');
    }
  };

  const openEditDialog = (task: { id: string; title: string; prompt: string }) => {
    setEditingTask(task);
    setNewTitle(task.title);
    setNewPrompt(task.prompt);
    setShowEditDialog(true);
  };

  const startAddTask = () => {
    setShowTemplateDialog(true);
  };

  const useTemplate = (template: SmartNotesTemplate) => {
    setNewTitle(template.name);
    setNewPrompt(template.prompt);
    setShowTemplateDialog(false);
    setShowAddDialog(true);
  };

  const createFromScratch = () => {
    setNewTitle('');
    setNewPrompt('');
    setShowTemplateDialog(false);
    setShowAddDialog(true);
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-12 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Main settings card */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-border/60 flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Smart Notes</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              AI-powered research that automatically gathers public information about companies. Powered by Google Gemini 2.5 Flash with zero data retention.
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5 min-w-0">
              <Label htmlFor="enable-smart-notes" className="text-sm font-medium">Enable Smart Notes</Label>
              <p className="text-xs text-muted-foreground">Allow AI research for new contacts.</p>
            </div>
            <Switch id="enable-smart-notes" checked={enableSmartNotes} onCheckedChange={handleToggleSmartNotes} />
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5 min-w-0">
              <Label htmlFor="auto-run" className="text-sm font-medium">Auto-run research</Label>
              <p className="text-xs text-muted-foreground">Run automatically when enhancing contacts.</p>
            </div>
            <Switch
              id="auto-run"
              checked={autoRunSmartNotes}
              onCheckedChange={toggleAutoRun}
              disabled={!enableSmartNotes}
            />
          </div>
        </div>
      </div>

      {/* Research tasks */}
      {enableSmartNotes && (
        <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-border/60 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold">Research tasks</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Define custom research prompts <span className="font-medium text-foreground">({tasks.length}/5)</span>
              </p>
            </div>
            <Button onClick={startAddTask} disabled={tasks.length >= 5} size="sm" className="gap-2 shadow-fluent-2">
              <Plus className="h-4 w-4" />
              Add task
            </Button>
          </div>

          <div className="p-5 sm:p-6">
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No research tasks yet.</p>
                <p className="text-xs mt-1">Create your first task to get started.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className={`relative rounded-lg border p-4 transition-all ${
                      task.is_default
                        ? 'border-success/40 bg-success/5'
                        : 'border-border/60 bg-card hover:border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold">{task.title}</h4>
                          {task.is_default && (
                            <Badge className="text-[10px] h-5 px-1.5 bg-success/15 text-success border border-success/30 hover:bg-success/20">
                              Active
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground italic break-words">"{task.prompt}"</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{task.prompt.length}/150 characters</p>
                      </div>

                      <div className="flex gap-1.5 shrink-0">
                        {!task.is_default && (
                          <Button variant="outline" size="sm" onClick={() => setDefaultTask(task.id)} className="h-7 text-xs">
                            Activate
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(task)} className="h-7 px-2 text-xs">
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => deleteTask(task.id)} className="h-7 px-2 text-xs">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Choose a Research Template
            </DialogTitle>
            <DialogDescription>
              Select a pre-made template or create your own from scratch
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-4">
            {SMART_NOTES_TEMPLATES.map((template, index) => (
              <button
                key={index}
                type="button"
                className="relative text-left rounded-xl border border-border/60 bg-card/60 backdrop-blur-md
                           p-4 hover:border-primary/50 hover:bg-card hover:shadow-fluent-8 transition-all cursor-pointer
                           group"
                onClick={() => useTemplate(template)}
              >
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    {template.name}
                  </h3>
                  <p className="text-xs text-muted-foreground italic">"{template.prompt}"</p>
                  <p className="text-[10px] text-muted-foreground">{template.description}</p>
                  <span className="inline-flex items-center text-[11px] text-primary font-medium mt-1 group-hover:underline">
                    Use template →
                  </span>
                </div>
              </button>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={createFromScratch}>
              Create from scratch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Research Task</DialogTitle>
            <DialogDescription>
              Create a custom AI research prompt for company information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">Task Title</Label>
              <Input
                id="task-title"
                placeholder="e.g., Latest News"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-prompt">Research Prompt</Label>
              <Textarea
                id="task-prompt"
                placeholder="e.g., Find and summarize the latest press releases and news about this company"
                value={newPrompt}
                onChange={(e) => setNewPrompt(e.target.value)}
                maxLength={150}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {newPrompt.length}/150 characters
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!newTitle.trim() || !newPrompt.trim()}
            >
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Research Task</DialogTitle>
            <DialogDescription>
              Update your AI research prompt
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-task-title">Task Title</Label>
              <Input
                id="edit-task-title"
                placeholder="e.g., Latest News"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-task-prompt">Research Prompt</Label>
              <Textarea
                id="edit-task-prompt"
                placeholder="e.g., Find and summarize the latest press releases and news about this company"
                value={newPrompt}
                onChange={(e) => setNewPrompt(e.target.value)}
                maxLength={150}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {newPrompt.length}/150 characters
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={!newTitle.trim() || !newPrompt.trim()}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}